/**
 * AddPriceListModal — Create a new Item Price for a different Price List.
 *
 * Opens from the "+ Add price list" button at the bottom of the Pricing Card.
 * Uses LinkFieldSearch for the Price List picker, auto-fetches currency,
 * and delegates all validation to ERPNext's native Item Price before_save.
 *
 * Part of Plan 170 (Item Pricing Table Redesign), Epic 03.
 */

import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { Loader2, AlertTriangle } from "lucide-react";
import { LinkFieldSearch } from "@/components/filter-builder/LinkFieldSearch";
import { frappeMethod } from "@/lib/erp/utils";
import { useAddItemPrice } from "@/features/inventory/api/useAddItemPrice";
import { toast } from "sonner";

// ── Props ────────────────────────────────────────────────────

interface AddPriceListModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemCode: string;
  stockUom: string;
  existingPriceLists: string[];
}

// ── Helpers ──────────────────────────────────────────────────

function getCurrencySymbol(currency: string): string {
  if (currency === "THB") return "฿";
  if (currency === "USD") return "$";
  if (currency === "EUR") return "€";
  return currency;
}

// ── Component ────────────────────────────────────────────────

export function AddPriceListModal({
  open,
  onOpenChange,
  itemCode,
  stockUom,
  existingPriceLists,
}: AddPriceListModalProps) {
  // Form state
  const [selectedPriceList, setSelectedPriceList] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string>("");
  const [rate, setRate] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validUpto, setValidUpto] = useState("");
  const [isFetchingCurrency, setIsFetchingCurrency] = useState(false);

  const mutation = useAddItemPrice(itemCode);

  // Reset form state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedPriceList(null);
      setCurrency("");
      setRate("");
      setValidFrom("");
      setValidUpto("");
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch currency when price list changes
  useEffect(() => {
    if (!selectedPriceList) {
      setCurrency("");
      return;
    }

    let cancelled = false;
    setIsFetchingCurrency(true);

    frappeMethod<{ currency: string }>("frappe.client.get_value", {
      doctype: "Price List",
      fieldname: "currency",
      filters: { name: selectedPriceList },
    })
      .then((result) => {
        if (!cancelled) {
          setCurrency(result?.currency || "");
        }
      })
      .catch(() => {
        if (!cancelled) setCurrency("");
      })
      .finally(() => {
        if (!cancelled) setIsFetchingCurrency(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedPriceList]);

  // Derived state
  const isDuplicate = selectedPriceList
    ? existingPriceLists.includes(selectedPriceList)
    : false;
  const parsedRate = parseFloat(rate);
  const isValid =
    !!selectedPriceList &&
    !isDuplicate &&
    !isNaN(parsedRate) &&
    parsedRate > 0;

  const currencySymbol = currency ? getCurrencySymbol(currency) : "—";

  // Save handler
  const handleSave = useCallback(() => {
    if (!isValid || !selectedPriceList) return;

    mutation.mutate(
      {
        item_code: itemCode,
        price_list: selectedPriceList,
        rate: parsedRate,
        uom: stockUom || undefined,
        valid_from: validFrom || undefined,
        valid_upto: validUpto || undefined,
      },
      {
        onSuccess: (response) => {
          toast.success(
            `Price added for ${response.price_list}`
          );
          onOpenChange(false);
        },
      }
    );
  }, [
    isValid,
    selectedPriceList,
    itemCode,
    parsedRate,
    stockUom,
    validFrom,
    validUpto,
    mutation,
    onOpenChange,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && isValid && !mutation.isPending) {
        handleSave();
      }
    },
    [isValid, mutation.isPending, handleSave]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "sm:max-w-[480px] sm:rounded-2xl",
          "max-sm:fixed max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:top-auto",
          "max-sm:translate-x-0 max-sm:translate-y-0",
          "max-sm:rounded-t-3xl max-sm:rounded-b-none",
          "max-sm:data-[state=open]:slide-in-from-bottom",
          "max-sm:data-[state=closed]:slide-out-to-bottom"
        )}
      >
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-lg font-semibold">
            Add price list
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Add a new selling price for this item
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-6 space-y-4" onKeyDown={handleKeyDown}>
          {/* Price List picker */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Price List
            </label>
            <LinkFieldSearch
              doctype="Price List"
              value={selectedPriceList}
              onChange={(val) => setSelectedPriceList(val)}
              placeholder="Search price lists..."
              defaultFilters={{ selling: 1 }}
              fetchInitialItems
              className="w-full text-sm"
            />
          </div>

          {/* Duplicate warning */}
          {isDuplicate && (
            <Alert className="border-amber-500/30 bg-amber-50 dark:bg-amber-950/20">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-sm text-amber-800 dark:text-amber-300">
                This price list already has a price for this item.
              </AlertDescription>
            </Alert>
          )}

          {/* Currency (read-only) */}
          {selectedPriceList && !isDuplicate && (
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                Currency
              </span>
              <span className="text-sm text-foreground tabular-nums">
                {isFetchingCurrency ? "..." : currency || "—"}
              </span>
            </div>
          )}

          {/* Rate input */}
          {selectedPriceList && !isDuplicate && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                Rate
              </label>
              <div className="flex items-center gap-2">
                <span
                  className="text-xl font-semibold text-muted-foreground select-none shrink-0"
                  aria-hidden="true"
                >
                  {currencySymbol}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  autoFocus
                  value={rate}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "" || /^\d*\.?\d*$/.test(v)) {
                      setRate(v);
                    }
                  }}
                  className={cn(
                    "w-full text-xl font-semibold tabular-nums",
                    "bg-transparent border-0 border-b-2 border-border/40",
                    "focus:border-[#007AFF] focus:outline-none",
                    "py-2 transition-colors",
                    "placeholder:text-muted-foreground/40"
                  )}
                  placeholder="0.00"
                  aria-label={`Price in ${currency || "selected currency"}`}
                  disabled={mutation.isPending}
                />
              </div>
            </div>
          )}

          {/* Optional date fields */}
          {selectedPriceList && !isDuplicate && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Valid from
                </label>
                <input
                  type="date"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                  className={cn(
                    "w-full text-sm bg-transparent border border-border/60 rounded-lg",
                    "px-3 py-2 focus:border-[#007AFF] focus:outline-none",
                    "text-foreground"
                  )}
                  disabled={mutation.isPending}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Valid upto
                </label>
                <input
                  type="date"
                  value={validUpto}
                  onChange={(e) => setValidUpto(e.target.value)}
                  className={cn(
                    "w-full text-sm bg-transparent border border-border/60 rounded-lg",
                    "px-3 py-2 focus:border-[#007AFF] focus:outline-none",
                    "text-foreground"
                  )}
                  disabled={mutation.isPending}
                />
              </div>
            </div>
          )}

          {/* Error alert — inline, modal stays open */}
          {mutation.isError && (
            <Alert variant="destructive">
              <AlertDescription>
                {mutation.error?.message || "Failed to add price. Please try again."}
              </AlertDescription>
            </Alert>
          )}

          {/* Footer buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
              className="flex-1 min-h-[44px] rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isValid || mutation.isPending || isFetchingCurrency}
              className="flex-1 min-h-[44px] rounded-full text-white font-semibold"
              style={{
                backgroundColor:
                  isValid && !mutation.isPending ? "#007AFF" : undefined,
              }}
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
